import React from "react";
import {
    Card,
    CardContent,
    Chip,
    Divider,
    TableContainer,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";

import type { SubscriptionHistoryItemDto } from "../../models/License";

type SubscriptionHistoryTableProps = {
    items: SubscriptionHistoryItemDto[];
};

const formatDate = (value?: string | null) => {
    if (!value) return "";
    return new Date(value).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
};

const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value ?? 0);

const getStatusColor = (
    status: string
): "default" | "success" | "warning" | "error" | "info" => {
    switch (status.toUpperCase()) {
        case "ACTIVE":
            return "success";
        case "SCHEDULED":
            return "info";
        case "EXPIRED":
            return "default";
        case "SUPERSEDED":
            return "warning";
        default:
            return "default";
    }
};

// Stripe rows show the price for their billing period; manual rows keep the annual price.
const formatPrice = (item: SubscriptionHistoryItemDto) =>
    item.billingInterval
        ? `${formatCurrency(item.price ?? 0)} / ${item.billingInterval === "month" ? "month" : "year"}`
        : formatCurrency(item.annualPrice);

const formatBilling = (item: SubscriptionHistoryItemDto) =>
    item.billingInterval === "month" ? "Monthly" : item.billingInterval === "year" ? "Annual" : "—";

const SubscriptionHistoryTable: React.FC<SubscriptionHistoryTableProps> = ({ items }) => {
    return (
        <Card sx={{
            height: "100%",
            width: "100%",
            borderWidth: 2,
            borderStyle: "solid",
            position: "relative",
            mx: 0,
            maxWidth: "none",
        }}>
            <CardContent>
                <Typography variant="h6">Subscription History</Typography>
                <Divider sx={{ my: 1.5 }} />
                <TableContainer sx={{ overflowX: "auto" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Tier</TableCell>
                                <TableCell>Plan</TableCell>
                                <TableCell>Billing</TableCell>
                                <TableCell>Start</TableCell>
                                <TableCell>End</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Price</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map(item => (
                                <TableRow key={item.tenantLicenseId}>
                                    <TableCell>{item.tierName}</TableCell>
                                    <TableCell>{item.planName}</TableCell>
                                    <TableCell>{formatBilling(item)}</TableCell>
                                    <TableCell>{formatDate(item.effectiveFromUtc)}</TableCell>
                                    <TableCell>{formatDate(item.effectiveToUtc)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={item.status}
                                            color={getStatusColor(item.status)}
                                            size="small"
                                            variant={item.status.toUpperCase() === "ACTIVE" ? "filled" : "outlined"}
                                        />
                                    </TableCell>
                                    <TableCell align="right">{formatPrice(item)}</TableCell>
                                </TableRow>
                            ))}

                            {items.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7}>
                                        <Typography variant="body2" color="text.secondary">
                                            No subscription history available.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Card>
    );
};

export default SubscriptionHistoryTable;
